import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { AppDataService, PromosService, PromotionMaterialsService } from 'app/services';

enum PromotionMaterialPlacement {
    Carousel = 1,
    NewsAndUpdates = 2,
    StorySlideshow = 3
}

enum PromotionMaterialBehavior {
    Informational = 1,
    Bookable = 2
}

@Component({
    selector: 'promotion-materials',
    templateUrl: './promotion-materials.component.html',
    styleUrls: ['./promotion-materials.component.scss'],
    providers: [MessageService]
})
export class PromotionMaterialsComponent implements OnInit {
    readonly placementEnum = PromotionMaterialPlacement;
    readonly maxImageSizeBytes = 5 * 1024 * 1024;

    // Story Slideshow placement rules (sc-39465).
    readonly storySlideMaxImageSizeBytes = 1024 * 1024;
    readonly storySlideImageWidth = 343;
    readonly storySlideImageHeight = 248;
    readonly storySlideHeaderTextMaxLength = 40;
    readonly storySlideCtaTextMaxLength = 30;
    readonly storySlideImageHelp = 'Exactly 343 × 248 px, JPG, PNG or WebP. Maximum 1 MB.';
    readonly storySlideImageError = 'Image must be exactly 343 × 248 px, JPG, PNG or WebP, 1 MB maximum.';

    readonly placements = [
        { label: 'Horizontal carousel', value: PromotionMaterialPlacement.Carousel },
        { label: 'News & Updates', value: PromotionMaterialPlacement.NewsAndUpdates },
        { label: 'Story Slideshow', value: PromotionMaterialPlacement.StorySlideshow }
    ];

    readonly behaviors = [
        { label: 'Informational', value: PromotionMaterialBehavior.Informational },
        { label: 'Bookable', value: PromotionMaterialBehavior.Bookable }
    ];

    readonly activityOptions = [
        { label: 'All', value: null },
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
    ];

    materials: any[] = [];
    promos: any[] = [];
    /** Active Region Module zones offered under Location for Story Slideshow materials. */
    zones: any[] = [];
    totalRecords = 0;
    rows = 50;
    first = 0;
    loading = false;
    saving = false;
    editorVisible = false;

    filter: any = {
        SearchName: '',
        Placement: null,
        Behavior: null,
        IsActive: null
    };

    model: any = this.createEmptyModel();
    bannerFile: File | null = null;
    modalFile: File | null = null;
    /** When true, save clears a carousel modal image that is already stored. */
    clearModalImage = false;
    bannerPreview = '';
    modalPreview = '';
    deeplinkError = '';

    @ViewChild('bannerImageInput') bannerImageInput?: ElementRef<HTMLInputElement>;
    @ViewChild('modalImageInput') modalImageInput?: ElementRef<HTMLInputElement>;
    @ViewChild('storyImageInput') storyImageInput?: ElementRef<HTMLInputElement>;

    constructor(
        private messageService: MessageService,
        private promotionMaterialsService: PromotionMaterialsService,
        private promosService: PromosService,
        private appData: AppDataService) {
    }

    ngOnInit(): void {
        this.loadPromos();
        this.loadZones();
    }

    get isStorySlide(): boolean {
        return this.model.Placement === PromotionMaterialPlacement.StorySlideshow;
    }

    get headerTextRemaining(): number {
        return Math.max(0, this.storySlideHeaderTextMaxLength - (this.model.HeaderText || '').length);
    }

    get ctaTextRemaining(): number {
        return Math.max(0, this.storySlideCtaTextMaxLength - (this.model.CtaText || '').length);
    }

    load(event?: TableLazyLoadEvent): void {
        this.loading = true;
        const skip = event?.first || 0;
        const take = event?.rows || this.rows;

        const request = {
            ...this.filter,
            Skip: skip,
            Take: take,
            SortField: event?.sortField || 'DisplayOrder',
            SortOrder: event?.sortOrder || 1
        };

        this.promotionMaterialsService.getPromotionMaterials(request, this.messageService)
            .then(data => {
                this.loading = false;
                if (!data?.Success) {
                    this.showError(data?.ErrorMessage || 'Unable to load promotion materials.');
                    return;
                }

                this.first = skip;
                this.rows = take;
                this.totalRecords = data.TotalRecords;
                this.materials = data.Items || [];
            })
            .catch(() => {
                this.loading = false;
                this.showError('Unable to load promotion materials.');
            });
    }

    applyFilters(): void {
        this.load();
    }

    clearFilters(): void {
        this.filter = {
            SearchName: '',
            Placement: null,
            Behavior: null,
            IsActive: null
        };
        this.load();
    }

    create(): void {
        this.model = this.createEmptyModel();
        this.clearSelectedImages();
        this.clearModalImage = false;
        this.bannerPreview = '';
        this.modalPreview = '';
        this.deeplinkError = '';
        this.loadZones();
        this.editorVisible = true;
    }

    edit(material: any): void {
        this.model = {
            Id: material.Id,
            InternalName: material.InternalName,
            Title: material.Title || '',
            HtmlViewDescription: material.HtmlViewDescription || '',
            HeaderText: material.HeaderText || '',
            CtaText: material.CtaText || '',
            DeeplinkUrl: material.DeeplinkUrl || '',
            GeoZoneIds: (material.Locations || []).map((location: any) => location.GeoZoneId),
            Placement: material.Placement,
            Behavior: material.Behavior,
            XpressPromoId: material.XpressPromoId,
            DisplayOrder: material.DisplayOrder,
            IsActive: material.IsActive,
            PromoLifecycleStatus: material.PromoLifecycleStatus,
            PromoName: material.PromoName,
            PromoCode: material.PromoCode,
            PromoStartedAt: material.PromoStartedAt,
            PromoValidUntil: material.PromoValidUntil,
            OriginalPlacement: material.Placement,
            OriginalBannerImageUrl: material.BannerImageUrl,
            OriginalModalImageUrl: material.ModalImageUrl
        };
        this.applyPlacementBehavior();
        this.ensureCurrentPromoOption();
        this.clearSelectedImages();
        this.clearModalImage = false;
        this.bannerPreview = material.BannerImageUrl;
        this.modalPreview = material.ModalImageUrl;
        this.deeplinkError = '';
        // Re-read zones so renamed/deleted Region Module zones are reflected when the material is opened.
        this.loadZones();
        this.editorVisible = true;
    }

    placementChanged(): void {
        // A file picked under the previous placement's rules (343 × 248 story image vs. free-size banner)
        // is dropped. Saved images are shown again only if the new placement uses the same image rules.
        this.clearSelectedImages();
        this.clearModalImage = false;
        this.deeplinkError = '';

        const originalIsStorySlide = this.model.OriginalPlacement === PromotionMaterialPlacement.StorySlideshow;
        const sameImageRules = this.model.OriginalPlacement != null && originalIsStorySlide === this.isStorySlide;
        this.bannerPreview = sameImageRules ? (this.model.OriginalBannerImageUrl || '') : '';
        this.modalPreview = sameImageRules && !this.isStorySlide ? (this.model.OriginalModalImageUrl || '') : '';
        this.applyPlacementBehavior();
    }

    /** Carousel is bookable. News & Updates and Story Slideshow are informational. */
    private behaviorForPlacement(placement: PromotionMaterialPlacement): PromotionMaterialBehavior {
        return placement === PromotionMaterialPlacement.Carousel
            ? PromotionMaterialBehavior.Bookable
            : PromotionMaterialBehavior.Informational;
    }

    private applyPlacementBehavior(): void {
        this.model.Behavior = this.behaviorForPlacement(this.model.Placement);
        if (this.model.Behavior !== PromotionMaterialBehavior.Bookable) {
            this.model.XpressPromoId = null;
            this.model.PromoLifecycleStatus = '';
        }
    }

    promoChanged(): void {
        const promo = this.promos.find(x => x.Id === this.model.XpressPromoId);
        if (!promo) {
            this.model.PromoLifecycleStatus = '';
            return;
        }

        const now = new Date();
        const startedAt = promo.StartedAt ? new Date(promo.StartedAt) : null;
        const validUntil = promo.ValidUntil ? new Date(promo.ValidUntil) : null;

        if (promo.PromotionMaterialLifecycleStatus === 'Deleted') {
            this.model.PromoLifecycleStatus = 'Deleted';
        } else if (!promo.IsActive) {
            this.model.PromoLifecycleStatus = 'Inactive';
        } else if (startedAt && startedAt > now) {
            this.model.PromoLifecycleStatus = 'Upcoming';
        } else if (validUntil && validUntil <= now) {
            this.model.PromoLifecycleStatus = 'Expired';
        } else {
            this.model.PromoLifecycleStatus = 'Available';
        }
    }

    deeplinkChanged(): void {
        const value = (this.model.DeeplinkUrl || '').trim();
        this.deeplinkError = value && !this.isValidAppDeeplink(value)
            ? 'Enter a valid Xpress User App deeplink (xpress://…, xpresseveryday://…, https://xpress.onelink.me/…, or https://www.xpress.ph/…).'
            : '';
    }

    imageSelected(event: Event, imageType: 'banner' | 'modal'): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) {
            return;
        }

        if (!this.hasAllowedImageType(file)) {
            this.showError('Images must be JPG, PNG, or WebP.');
            input.value = '';
            return;
        }

        if (file.size > this.maxImageSizeBytes) {
            this.showError('Images cannot exceed 5 MB.');
            input.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            if (imageType === 'banner') {
                this.bannerFile = file;
                this.bannerPreview = String(reader.result || '');
            } else {
                this.modalFile = file;
                this.modalPreview = String(reader.result || '');
                this.clearModalImage = false;
            }
        };
        reader.readAsDataURL(file);
    }

    /** Drops a carousel's modal image. Save sends ClearModalImage so the stored URL is cleared. */
    removeModalImage(): void {
        this.modalFile = null;
        this.modalPreview = '';
        this.clearModalImage = true;
        if (this.modalImageInput) {
            this.modalImageInput.nativeElement.value = '';
        }
    }

    /** Story slide image: rejected before save unless JPG/PNG/WebP, ≤ 1 MB and exactly 343 × 248 px. */
    storyImageSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) {
            return;
        }

        if (!this.hasAllowedImageType(file) || file.size > this.storySlideMaxImageSizeBytes) {
            this.showError(this.storySlideImageError);
            input.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result || '');
            const image = new Image();
            image.onload = () => {
                if (image.naturalWidth !== this.storySlideImageWidth
                    || image.naturalHeight !== this.storySlideImageHeight) {
                    this.showError(
                        `${this.storySlideImageError} Selected image is ${image.naturalWidth} × ${image.naturalHeight} px.`);
                    input.value = '';
                    return;
                }

                this.bannerFile = file;
                this.bannerPreview = dataUrl;
            };
            image.onerror = () => {
                this.showError(this.storySlideImageError);
                input.value = '';
            };
            image.src = dataUrl;
        };
        reader.readAsDataURL(file);
    }

    save(): void {
        this.applyPlacementBehavior();
        const validationError = this.isStorySlide ? this.validateStorySlide() : this.validate();
        if (validationError) {
            this.showError(validationError);
            return;
        }

        const formData = new FormData();
        formData.append('Id', String(this.model.Id || 0));
        formData.append('InternalName', this.model.InternalName.trim());
        formData.append('Placement', String(this.model.Placement));
        formData.append('DisplayOrder', String(this.model.DisplayOrder || 0));
        formData.append('IsActive', String(!!this.model.IsActive));
        for (const zoneId of this.model.GeoZoneIds || []) {
            formData.append('GeoZoneIds', String(zoneId));
        }

        if (this.isStorySlide) {
            // Only Story Slideshow fields are sent; behavior/promo/modal are not part of this placement.
            formData.append('Behavior', String(this.behaviorForPlacement(this.model.Placement)));
            formData.append('Title', '');
            formData.append('HtmlViewDescription', '');
            formData.append('HeaderText', (this.model.HeaderText || '').trim());
            formData.append('CtaText', (this.model.CtaText || '').trim());
            formData.append('DeeplinkUrl', (this.model.DeeplinkUrl || '').trim());
            if (this.bannerFile) {
                formData.append('BannerImage', this.bannerFile);
            }
        } else {
            formData.append('Behavior', String(this.model.Behavior));
            formData.append('Title', this.model.Behavior === PromotionMaterialBehavior.Informational
                ? (this.model.Title || '').trim()
                : '');
            formData.append('HtmlViewDescription', this.model.Behavior === PromotionMaterialBehavior.Informational
                ? (this.model.HtmlViewDescription || '')
                : '');
            formData.append('HeaderText', '');
            formData.append('CtaText', '');
            formData.append('DeeplinkUrl', '');
            if (this.model.Behavior === PromotionMaterialBehavior.Bookable && this.model.XpressPromoId) {
                formData.append('XpressPromoId', String(this.model.XpressPromoId));
            }
            if (this.bannerFile) {
                formData.append('BannerImage', this.bannerFile);
            }
            if (this.modalFile) {
                formData.append('ModalImage', this.modalFile);
            } else if (this.clearModalImage && this.model.Placement === PromotionMaterialPlacement.Carousel) {
                formData.append('ClearModalImage', 'true');
            }
        }

        this.saving = true;
        this.promotionMaterialsService.addOrUpdatePromotionMaterial(formData, this.messageService)
            .then(data => {
                this.saving = false;
                if (!data?.Success) {
                    this.showError(data?.ErrorMessage || 'Unable to save promotion material.');
                    return;
                }

                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Promotion material saved.'
                });
                this.clearSelectedImages();
                this.editorVisible = false;
                this.load();
            })
            .catch(() => {
                this.saving = false;
                this.showError('Unable to save promotion material.');
            });
    }

    delete(material: any): void {
        if (!window.confirm(`Delete "${material.InternalName}"?`)) {
            return;
        }

        this.promotionMaterialsService.deletePromotionMaterial(
            { Id: material.Id },
            this.messageService)
            .then(data => {
                if (!data?.Success) {
                    this.showError(data?.ErrorMessage || 'Unable to delete promotion material.');
                    return;
                }

                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Promotion material deleted.'
                });
                this.load();
            })
            .catch(() => this.showError('Unable to delete promotion material.'));
    }

    placementName(value: PromotionMaterialPlacement): string {
        return this.placements.find(x => x.value === value)?.label || '';
    }

    behaviorName(value: PromotionMaterialBehavior): string {
        return this.behaviors.find(x => x.value === value)?.label || '';
    }

    isStorySlideMaterial(material: any): boolean {
        return material?.Placement === PromotionMaterialPlacement.StorySlideshow;
    }

    /** Customer-facing text shown in the list: informational title, or story slide header text. */
    displayTitle(material: any): string {
        if (material.Title) {
            return material.Title;
        }
        if (this.isStorySlideMaterial(material) && material.HeaderText) {
            return material.HeaderText;
        }
        return '—';
    }

    isUnavailablePromoStatus(status: string): boolean {
        return !!status && status !== 'Available' && status !== 'Upcoming';
    }

    /** Location column: zone names of a Story Slideshow material. */
    locationNames(material: any): string {
        const names = (material?.Locations || []).map((location: any) => location.Name).filter((name: string) => !!name);
        return names.length ? names.join(', ') : '—';
    }

    private loadZones(): void {
        this.promosService.getGeoZonesForPromo({
            ClientLocationType: this.appData.getClientLocationType()
        }, this.messageService).then(data => {
            if (!data?.Success) {
                return;
            }

            this.zones = (data.Items || [])
                .filter((zone: any) => zone.IsEnabled !== false)
                .sort((a: any, b: any) => String(a.Name).localeCompare(String(b.Name)));

            // A zone deleted in the Region Module is no longer selectable and drops off the material (scenario 8).
            const available = new Set(this.zones.map((zone: any) => zone.Id));
            this.model.GeoZoneIds = (this.model.GeoZoneIds || []).filter((id: number) => available.has(id));
        });
    }

    private loadPromos(): void {
        this.promosService.getPromos({
            SearchName: '',
            Skip: 0,
            Take: 1000,
            SortField: 'Name',
            SortOrder: 1,
            IsActive: true,
            ClientLocationType: this.appData.getClientLocationType()
        }, this.messageService).then(data => {
            if (!data?.Success) {
                return;
            }

            this.promos = (data.Items || [])
                .filter((promo: any) => promo.IsActive && !promo.IsDeleted)
                .map((promo: any) => ({
                    ...promo,
                    PromotionMaterialLabel: `${promo.Code} — ${promo.Name}`
                }));
            this.ensureCurrentPromoOption();
            if (this.editorVisible && this.model.XpressPromoId) {
                this.promoChanged();
            }
        });
    }

    private validate(): string | null {
        if (!this.model.InternalName?.trim()) {
            return 'Internal name is required.';
        }
        if (!this.model.Placement) {
            return 'Placement is required.';
        }
        if (!this.model.Behavior) {
            return 'Behavior is required.';
        }
        if (this.model.DisplayOrder == null || this.model.DisplayOrder < 0) {
            return 'Display order cannot be negative.';
        }
        if (!this.model.GeoZoneIds?.length) {
            return 'Location is required.';
        }
        if (this.model.Behavior === PromotionMaterialBehavior.Informational && !this.model.Title?.trim()) {
            return 'Title is required for informational material.';
        }
        if (this.model.Behavior === PromotionMaterialBehavior.Informational && !this.hasHtmlContent(this.model.HtmlViewDescription)) {
            return 'Description is required for informational material.';
        }
        const movedFromStorySlide = this.model.OriginalPlacement === PromotionMaterialPlacement.StorySlideshow;
        const needsNewImages = !this.model.Id || movedFromStorySlide;
        const modalImageRequired = this.model.Placement !== PromotionMaterialPlacement.Carousel;
        if (needsNewImages && !this.bannerFile) {
            return 'Banner image is required.';
        }
        if (modalImageRequired && !this.modalFile && (needsNewImages || this.clearModalImage || !this.model.OriginalModalImageUrl)) {
            return 'Modal image is required.';
        }
        return null;
    }

    /** Names every empty required field in one message (sc-39465 scenario 4). */
    private validateStorySlide(): string | null {
        const missing: string[] = [];
        if (!this.model.InternalName?.trim()) {
            missing.push('Internal name');
        }
        const needsImage = !this.model.Id
            || this.model.OriginalPlacement !== PromotionMaterialPlacement.StorySlideshow;
        if (needsImage && !this.bannerFile) {
            missing.push('Image');
        }
        if (!this.model.GeoZoneIds?.length) {
            missing.push('Location');
        }

        if (missing.length === 1) {
            return `${missing[0]} is required.`;
        }
        if (missing.length > 1) {
            return `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]} are required.`;
        }

        if ((this.model.HeaderText || '').trim().length > this.storySlideHeaderTextMaxLength) {
            return `Header text cannot exceed ${this.storySlideHeaderTextMaxLength} characters.`;
        }
        if ((this.model.CtaText || '').trim().length > this.storySlideCtaTextMaxLength) {
            return `CTA text cannot exceed ${this.storySlideCtaTextMaxLength} characters.`;
        }
        this.deeplinkChanged();
        if (this.deeplinkError) {
            return this.deeplinkError;
        }
        if (this.model.DisplayOrder == null || this.model.DisplayOrder < 0) {
            return 'Display order cannot be negative.';
        }
        return null;
    }

    /** Mirrors the server rule: app custom schemes, the OneLink host, or the marketing site and its paths. */
    private isValidAppDeeplink(value: string): boolean {
        let url: URL;
        try {
            url = new URL(value);
        } catch {
            return false;
        }

        const scheme = url.protocol.replace(/:$/, '').toLowerCase();
        if (scheme === 'xpress' || scheme === 'xpresseveryday') {
            // URL() leaves custom-scheme hosts in pathname (e.g. "//book-a-ride") on some browsers.
            const schemeHost = url.host || url.pathname.replace(/^\/\//, '').split(/[/?#]/)[0];
            return !!schemeHost;
        }
        const host = url.hostname.toLowerCase();
        return scheme === 'https' && (host === 'xpress.onelink.me' || host === 'www.xpress.ph');
    }

    private hasAllowedImageType(file: File): boolean {
        const extension = file.name.split('.').pop()?.toLowerCase();
        return !!extension
            && ['jpg', 'jpeg', 'png', 'webp'].includes(extension)
            && ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
    }

    private createEmptyModel(): any {
        return {
            Id: 0,
            InternalName: '',
            Title: '',
            HtmlViewDescription: '',
            HeaderText: '',
            CtaText: '',
            DeeplinkUrl: '',
            GeoZoneIds: [],
            Placement: PromotionMaterialPlacement.Carousel,
            Behavior: PromotionMaterialBehavior.Bookable,
            XpressPromoId: null,
            DisplayOrder: 1,
            IsActive: true,
            PromoLifecycleStatus: '',
            OriginalPlacement: null,
            OriginalBannerImageUrl: '',
            OriginalModalImageUrl: ''
        };
    }

    private ensureCurrentPromoOption(): void {
        if (!this.model.XpressPromoId
            || this.promos.some(promo => promo.Id === this.model.XpressPromoId)
            || !this.model.PromoName) {
            return;
        }

        this.promos = [
            ...this.promos,
            {
                Id: this.model.XpressPromoId,
                Name: this.model.PromoName,
                Code: this.model.PromoCode,
                StartedAt: this.model.PromoStartedAt,
                ValidUntil: this.model.PromoValidUntil,
                IsActive: this.model.PromoLifecycleStatus !== 'Inactive'
                    && this.model.PromoLifecycleStatus !== 'Deleted',
                PromotionMaterialLifecycleStatus: this.model.PromoLifecycleStatus,
                PromotionMaterialLabel: `${this.model.PromoCode} — ${this.model.PromoName}`
            }
        ];
    }

    private clearSelectedImages(): void {
        this.bannerFile = null;
        this.modalFile = null;
        for (const input of [this.bannerImageInput, this.modalImageInput, this.storyImageInput]) {
            if (input) {
                input.nativeElement.value = '';
            }
        }
    }

    private hasHtmlContent(value: string): boolean {
        if (!value) {
            return false;
        }

        const text = value.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
        return text.length > 0;
    }

    private showError(detail: string): void {
        this.messageService.add({ severity: 'error', summary: 'Error', detail });
    }
}
